// Hash-based SPA router for Sarathi.
// Mounts a route into a fixed overlay above the landing page.
// Empty hash -> overlay hidden, landing page visible.
// #/<route>[?params] -> overlay rendered with route module.

import type { RouteRenderer, RouteCleanup } from "./types.ts";

interface Registry {
  [hash: string]: RouteRenderer;
}

const registry: Registry = {};
let currentCleanup: RouteCleanup | null = null;
let host: HTMLElement | null = null;

export function registerRoute(name: string, renderer: RouteRenderer): void {
  registry[name] = renderer;
}

export function bootstrapRouter(targetHost: HTMLElement): void {
  host = targetHost;
  window.addEventListener("hashchange", handleHashChange);
  // Initial render
  handleHashChange();
}

function handleHashChange(): void {
  if (!host) return;
  const raw = window.location.hash.replace(/^#\/?/, "");
  if (!raw) {
    teardownCurrent();
    host.hidden = true;
    host.innerHTML = "";
    return;
  }
  const [path, query] = raw.split("?");
  const renderer = registry[path ?? ""];
  if (!renderer) {
    // Unknown route — fall back to home
    if (path !== "home") {
      window.location.hash = "#/home";
      return;
    }
    return;
  }
  teardownCurrent();
  host.hidden = false;
  host.innerHTML = "";
  const params = new URLSearchParams(query ?? "");
  try {
    currentCleanup = renderer(host, params);
  } catch (err) {
    console.error("[sarathi.router] route render error", err);
    host.innerHTML = `<div class="sarathi-route-shell"><h1>Maaf kijiye, kuch galat ho gaya.</h1><p>Please go back to the home screen.</p><a href="#/home">Wapas jaayein</a></div>`;
  }
}

function teardownCurrent(): void {
  if (currentCleanup) {
    try {
      currentCleanup();
    } catch (err) {
      console.warn("[sarathi.router] cleanup error", err);
    }
    currentCleanup = null;
  }
}

export function navigate(name: string): void {
  window.location.hash = `#/${name}`;
}

export function exitOverlay(): void {
  window.location.hash = "";
}
